import { guardChildNodeByType, xml2ast, allHtmlParsers, parseChildren, tableHtmlParsers, headingHtmlParsers, paragraphHtmlParsers, simpleHtmlParsers } from '@utils/ast';
import { configRefs } from '@utils/constants';
import { parseCaption, parseCaptionDisplayMode } from './util.parse-caption.js';
import { parsePm2Data } from './util.parse-pm2-data.js';

const parse23VideoComponent = (node) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i;
  const caption = parseCaption(node);
  const pm2 = parsePm2Data(node, caption);
  const twentyThreeVideo = node.querySelector("TwentyThreeVideo");
  if (!pm2) {
    return null;
  }
  const id = node.getAttribute("id") ?? void 0;
  if (!twentyThreeVideo || twentyThreeVideo.children.length === 0) {
    return null;
  }
  const twentyThreeId = removeCDataTag(
    (_a = twentyThreeVideo.querySelector("Id")) == null ? void 0 : _a.innerHTML
  );
  const twentyThreeTitle = removeCDataTag(
    (_b = twentyThreeVideo.querySelector("Title")) == null ? void 0 : _b.innerHTML
  );
  const twentyThreeDescription = removeCDataTag(
    (_c = twentyThreeVideo.querySelector("DescriptionText")) == null ? void 0 : _c.innerHTML
  );
  const token = removeCDataTag(
    (_d = twentyThreeVideo.querySelector("Token")) == null ? void 0 : _d.innerHTML
  );
  const thumbnail = removeCDataTag(
    (_e = twentyThreeVideo.querySelector("SmallThumbnail")) == null ? void 0 : _e.innerHTML
  );
  const width = Number(
    removeCDataTag((_f = twentyThreeVideo.querySelector("OriginalWidth")) == null ? void 0 : _f.innerHTML)
  );
  const height = Number(
    removeCDataTag((_g = twentyThreeVideo.querySelector("OriginalHeight")) == null ? void 0 : _g.innerHTML)
  );
  const aspectRatio = Number.isNaN(height) || Number.isNaN(width) ? void 0 : `${height / width * 100}%`;
  if (!twentyThreeId || !token || !thumbnail || !twentyThreeTitle) {
    return null;
  }
  return {
    nodeType: "23-video-component",
    id,
    pm2,
    meta: {
      caption: {
        ...caption,
        // merge video23 title and description with regular caption values.
        title: {
          showOutsideContext: false,
          showInContext: false,
          ...caption.title,
          value: ((_h = caption.title) == null ? void 0 : _h.value) || twentyThreeTitle || ""
        },
        description: {
          showOutsideContext: false,
          showInContext: false,
          ...caption.description,
          value: ((_i = caption.description) == null ? void 0 : _i.value) || twentyThreeDescription || ""
        }
      }
    },
    options: {
      displayCaption: parseCaptionDisplayMode(node),
      displayInternalInfoInPlayer: node.getAttribute("showOverlay") === "true",
      showSubtitlesByDefault: node.getAttribute("showSubtitlesAsDefault") === "true"
    },
    twentyThreeId,
    token,
    thumbnailUrl: thumbnail ? `${configRefs.apis.video23.video.dangerouslyAssertSyncValue()}${thumbnail}` : void 0,
    aspectRatio
  };
};
const parseAudioComponent = (node, context) => {
  const caption = parseCaption(node);
  const pm2 = parsePm2Data(node, caption);
  if (!pm2) {
    return null;
  }
  const id = node.getAttribute("id") ?? void 0;
  const src = node.getAttribute("referenceId");
  const imageSrc = node.getAttribute("imageSrc");
  const audioTranscriptNode = Array.from(node.childNodes).find(
    guardChildNodeByType("AudioTranscript")
  );
  if (!src) {
    return null;
  }
  return {
    nodeType: "audio-component",
    id,
    pm2: {
      ...pm2,
      transcript: audioTranscriptNode ? parseChildren(
        audioTranscriptNode,
        {
          ...simpleHtmlParsers,
          ...paragraphHtmlParsers,
          ...headingHtmlParsers,
          ...tableHtmlParsers
        },
        context.depth + 1
      ) : void 0
    },
    meta: {
      caption
    },
    options: {
      displayCaption: parseCaptionDisplayMode(node)
    },
    src,
    imageSrc: imageSrc ? `${configRefs.apis.pm2.media.dangerouslyAssertSyncValue()}${imageSrc}` : void 0
  };
};
const parse23AudioComponent = (node) => {
  var _a;
  const caption = parseCaption(node);
  const pm2 = parsePm2Data(node, caption);
  if (!pm2) {
    return null;
  }
  const id = node.getAttribute("id") ?? void 0;
  const twentyThreeVideoNode = Array.from(node.childNodes).find(
    guardChildNodeByType("TwentyThreeAudio")
  );
  if (!twentyThreeVideoNode) {
    return null;
  }
  const src = (_a = Array.from(twentyThreeVideoNode.childNodes).find(
    guardChildNodeByType("DownloadPath")
  )) == null ? void 0 : _a.textContent;
  if (!src) {
    return null;
  }
  const imageSrc = (() => {
    var _a2;
    const twentyThreeVideoSrc = (_a2 = Array.from(
      twentyThreeVideoNode.childNodes
    ).find(guardChildNodeByType("StandardThumbnail"))) == null ? void 0 : _a2.textContent;
    if (twentyThreeVideoSrc) {
      return `${configRefs.apis.video23.audio.dangerouslyAssertSyncValue()}${twentyThreeVideoSrc}`;
    }
    const sitecoreSrc = node.getAttribute("imageSrc");
    if (sitecoreSrc) {
      return `${configRefs.apis.pm2.media.dangerouslyAssertSyncValue()}${sitecoreSrc}`;
    }
  })();
  const audioTranscript = (() => {
    var _a2;
    try {
      return JSON.parse(
        ((_a2 = Array.from(twentyThreeVideoNode.childNodes).find(
          guardChildNodeByType("AudioTranscript")
        )) == null ? void 0 : _a2.textContent) ?? "null"
      );
    } catch {
      return void 0;
    }
  })();
  const audioString = audioTranscript == null ? void 0 : audioTranscript.map((it) => it.text.join("<br>")).join(" ");
  return {
    nodeType: "audio-component",
    id,
    pm2: {
      ...pm2,
      transcript: audioTranscript ? xml2ast(audioString, allHtmlParsers, "text/html") : void 0
    },
    meta: {
      caption
    },
    options: {
      displayCaption: parseCaptionDisplayMode(node)
    },
    src: `${configRefs.apis.video23.audio.dangerouslyAssertSyncValue()}${src}`,
    imageSrc
  };
};
const mediaComponentParsers = {
  audio: (node, context) => parse23AudioComponent(node) ?? parseAudioComponent(node, context),
  video: parse23VideoComponent
};
function removeCDataTag(input) {
  return input == null ? void 0 : input.replace("<![CDATA[", "").replace("]]>", "");
}

export { mediaComponentParsers };
//# sourceMappingURL=component.media.js.map
